import { useState } from "react";
import { InfoCircle, Kanban, Chart, Wallet, TickCircle, Element3, People, Calendar } from "iconsax-react";
import cn from "classnames";
import { Button, Toast } from "@/shared/ui";
import type { ProjectDetail } from "../../../types";

interface ProjectSidebarProps {
  project: ProjectDetail;
  canEdit: boolean;
  t: (key: string, params?: Record<string, unknown>) => string;
  sectionRefs: {
    hero: React.RefObject<HTMLDivElement | null>;
    kpi: React.RefObject<HTMLDivElement | null>;
    tasks: React.RefObject<HTMLDivElement | null>;
    members: React.RefObject<HTMLDivElement | null>;
    calendar: React.RefObject<HTMLDivElement | null>;
    budget: React.RefObject<HTMLDivElement | null>;
    similar: React.RefObject<HTMLDivElement | null>;
  };
}

function formatDuration(
  startDate: string | null,
  endDate: string | null,
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  if (!startDate || !endDate) return "";
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  const totalMonths = Math.round(diffMs / (1000 * 60 * 60 * 24 * 30.44));

  if (totalMonths >= 12) {
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    if (months === 0) return t("sidebar.durationYears", { count: years });
    return t("sidebar.durationYearsMonths", { years, months });
  }
  return t("sidebar.durationMonths", { count: totalMonths });
}

const NAV_ITEMS = [
  { id: "hero", icon: InfoCircle, labelKey: "sidebar.navAbout", alwaysVisible: true },
  { id: "kpi", icon: Chart, labelKey: "sidebar.navKpi", alwaysVisible: false },
  { id: "tasks", icon: Kanban, labelKey: "sidebar.navWorkspace", alwaysVisible: false },
  { id: "members", icon: People, labelKey: "sidebar.navMembers", alwaysVisible: true },
  { id: "budget", icon: Wallet, labelKey: "sidebar.navBudget", alwaysVisible: true },
  { id: "calendar", icon: Calendar, labelKey: "sidebar.navCalendar", alwaysVisible: false },
  { id: "similar", icon: Element3, labelKey: "sidebar.navSimilar", alwaysVisible: false },
] as const;

export default function ProjectSidebar({ project, canEdit, t, sectionRefs }: ProjectSidebarProps) {
  const [activeNav, setActiveNav] = useState("hero");

  const duration = formatDuration(project.start_date, project.end_date, t);

  const sectionVisibility: Record<string, boolean> = {
    hero: true,
    kpi: project.kpis.length > 0,
    tasks: project.recent_tasks.length > 0,
    members: true,
    calendar: project.recent_tasks.length > 0,
    budget: canEdit && !project.is_internal,
    similar: project.similar_projects.length > 0,
  };

  function handleNavClick(id: string) {
    setActiveNav(id);
    const ref = sectionRefs[id as keyof typeof sectionRefs];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <div className="w-2xs shrink-0 sticky top-0 flex flex-col gap-3">
      <div className="radius-3xl surface-base-fill p-5 flex flex-col items-center gap-4">
        <div className="flex flex-col items-center gap-1 text-center">
          <h4 className="text-body-bold-md content-base-primary">{project.name}</h4>
          {duration && (
            <p className="text-body-regular-sm content-base-secondary">{t("sidebar.projectDuration", { duration })}</p>
          )}
        </div>

        <div className="relative w-full px-3">
          <svg viewBox="0 0 200 110" className="w-full">
            <path
              d="M 15 100 A 85 85 0 0 1 185 100"
              fill="none"
              stroke="var(--color-grey-200)"
              strokeWidth="14"
              strokeLinecap="round"
            />
            <path
              d="M 15 100 A 85 85 0 0 1 185 100"
              fill="none"
              stroke="var(--color-green-500)"
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={`${0.92 * 267} ${267}`}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
            <span className="text-display-xs content-base-primary">92%</span>
            <span className="text-body-regular-xs content-base-secondary">{t("sidebar.overallScore")}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2.5 w-full">
          <div className="flex items-center gap-2">
            <TickCircle size={18} color="currentColor" className="content-action-positive shrink-0" />
            <span className="text-body-regular-sm content-base-primary">{t("sidebar.checkpoint1")}</span>
          </div>
          <div className="flex items-center gap-2">
            <TickCircle size={18} color="currentColor" className="content-action-positive shrink-0" />
            <span className="text-body-regular-sm content-base-primary">{t("sidebar.checkpoint2")}</span>
          </div>
          <div className="flex items-center gap-2">
            <TickCircle size={18} color="currentColor" className="content-action-positive shrink-0" />
            <span className="text-body-regular-sm content-base-primary">{t("sidebar.checkpoint3")}</span>
          </div>
        </div>
        <Toast color="notice" text={t("sidebar.devNotice")} closable={false} autoClose={false} isFullWidth />
        <Button variant="tertiary" size="md" className="w-full" type="button">
          {t("sidebar.details")}
        </Button>
      </div>

      <div className="radius-3xl surface-base-fill p-5 flex flex-col gap-1">
        {NAV_ITEMS.filter((item) => sectionVisibility[item.id]).map((item) => {
          const isActive = activeNav === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleNavClick(item.id)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 radius-md cursor-pointer text-left w-full outline-none",
                isActive ? "surface-component-fill" : "hover:surface-component-fill",
              )}>
              <item.icon
                size={18}
                color="currentColor"
                variant={isActive ? "Bold" : "Linear"}
                className={cn("shrink-0", isActive ? "content-action-brand" : "content-base-low")}
              />
              <span className={cn("text-label-sm", isActive ? "content-base-primary" : "content-base-low")}>
                {t(item.labelKey)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
